import { createContext, useContext, useEffect, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Container } from "@/components/Container";
import { Section } from "@/components/Section";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import {
  listPublishedWeeks,
  listPublishedWeekPreferences,
  getPublicWeeklyMenu,
  type PublishedWeek,
} from "@/lib/public/weekly-menu.functions";
import mealPlaceholder from "@/assets/meal-placeholder.jpg";
import { DishNutrition } from "@/components/mp2/DishNutrition";
import {
  getPublicDishNutrition,
  type DishNutritionMap,
} from "@/lib/public/dish-nutrition.functions";

const NutritionCtx = createContext<DishNutritionMap>({});

const searchSchema = z.object({
  // Optional: empty values are dropped so /weekly-menu stays a clean URL.
  plan: z.string().optional().catch(undefined).transform((v) => v || undefined),
  pref: z.string().optional().catch(undefined).transform((v) => v || undefined),
  week: z.string().optional().catch(undefined).transform((v) => v || undefined),
});
type WeeklyMenuSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/weekly-menu")({
  validateSearch: (raw): WeeklyMenuSearch => searchSchema.parse(raw ?? {}),
  head: () => ({
    meta: [
      { title: "Weekly Menu — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "See what's on the Meal Plans 2.0 menu this week and next — Monday to Sunday, by plan and food preference.",
      },
      { property: "og:title", content: "Weekly Menu — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content: "Published weekly meal plan menus, Monday to Sunday.",
      },
      { property: "og:url", content: "https://poonamkapur.com/weekly-menu" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/weekly-menu" }],
  }),
  component: WeeklyMenuPage,
});

const DAYS_FULL = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
const SLOTS = ["breakfast", "lunch", "snacks", "dinner"] as const;
type Slot = (typeof SLOTS)[number];

type DishView = {
  dish_id?: string | null;
  dish_name: string;
  image_url: string | null;
  description: string | null;
  alt_text: string | null;
};

type Cell = (DishView & {
  /** Every configured dish for the slot, in admin order (includes the first). */
  dishes?: DishView[];
}) | null;

type WeeklyMenuJson = {
  plan_slug?: string;
  plan_title?: string;
  food_preference_code?: string;
  week_start_date?: string;
  week_end_date?: string;
  published_at?: string | null;
  cells?: Array<{
    day_of_week: number;
    slot: Slot;
    dish_id: string;
    dish_name?: string | null;
    name?: string | null;
    dish_slug?: string | null;
    image_url?: string | null;
    alt_text?: string | null;
    description?: string | null;
  }>;
  days?: Array<{
    day_of_week: number;
    slots: Partial<
      Record<
        Slot,
        {
          dish_id: string;
          dish_name?: string | null;
          name?: string | null;
          image_url?: string | null;
          alt_text?: string | null;
          description?: string | null;
        }
      >
    >;
    slot_dishes?: Partial<
      Record<
        Slot,
        Array<{
          dish_id: string;
          name?: string | null;
          image_url?: string | null;
          alt_text?: string | null;
          description?: string | null;
          sort_order?: number;
        }>
      >
    >;
  }>;
};

// Stored menu days are zero-based (0 = Monday … 6 = Sunday), matching
// week_start_date + day_of_week. Display columns use the same zero-based order.
function dayIndexFromStored(raw: unknown): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return i >= 0 && i <= 6 ? i : null;
}

function normaliseGrid(menu: WeeklyMenuJson | null) {
  const grid: Record<number, Record<Slot, Cell>> = {};
  for (let d = 0; d < 7; d++) {
    grid[d] = { breakfast: null, lunch: null, snacks: null, dinner: null };
  }
  if (!menu) return grid;
  if (Array.isArray(menu.cells)) {
    for (const c of menu.cells) {
      const idx = dayIndexFromStored(c.day_of_week);
      if (idx === null) continue;
      if (!SLOTS.includes(c.slot)) continue;
      grid[idx][c.slot] = {
        dish_id: c.dish_id ?? null,
        dish_name: (c.dish_name ?? c.name ?? "").trim(),
        image_url: c.image_url ?? null,
        description: (c.description ?? "").trim() || null,
        alt_text: c.alt_text ?? null,
      };
    }
  } else if (Array.isArray(menu.days)) {
    for (const day of menu.days) {
      const idx = dayIndexFromStored(day.day_of_week);
      if (idx === null) continue;
      for (const s of SLOTS) {
        const all = (day.slot_dishes?.[s] ?? [])
          .slice()
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .map((d) => ({
            dish_id: d.dish_id ?? null,
            dish_name: (d.name ?? "").trim(),
            image_url: d.image_url ?? null,
            description: (d.description ?? "").trim() || null,
            alt_text: d.alt_text ?? null,
          }))
          .filter((d) => d.dish_name.length > 0);
        const first = all[0];
        const cell = day.slots?.[s];
        if (first) {
          grid[idx][s] = { ...first, dishes: all };
        } else if (cell) {
          grid[idx][s] = {
            dish_id: cell.dish_id ?? null,
            dish_name: (cell.dish_name ?? cell.name ?? "").trim(),
            image_url: cell.image_url ?? null,
            description: (cell.description ?? "").trim() || null,
            alt_text: cell.alt_text ?? null,
          };
        }
      }
    }
  }

  return grid;
}

function WeeklyMenuPage() {
  const { catalog, isLoading: catalogLoading } = usePublicMp2Catalog();
  const listWeeksFn = useServerFn(listPublishedWeeks);
  const listWeekPrefsFn = useServerFn(listPublishedWeekPreferences);
  const getMenuFn = useServerFn(getPublicWeeklyMenu);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/weekly-menu" });

  const plans = useMemo(
    () => (catalog?.plans ?? []).map((p) => ({ slug: p.slug, title: p.title, id: p.id })),
    [catalog],
  );
  const planSlug = search.plan || null;
  const preference = search.pref || null;
  const weekStart = search.week || null;

  const setPlanSlug = (v: string | null) =>
    navigate({
      search: (p: WeeklyMenuSearch) => ({ ...p, plan: v ?? undefined, pref: undefined, week: undefined }),
      replace: true,
    });
  const setPreference = (v: string | null) =>
    navigate({
      search: (p: WeeklyMenuSearch) => ({ ...p, pref: v ?? undefined, week: undefined }),
      replace: true,
    });
  const setWeekStart = (v: string | null) =>
    navigate({
      search: (p: WeeklyMenuSearch) => ({ ...p, week: v ?? undefined }),
      replace: true,
    });

  useEffect(() => {
    if (!planSlug && plans.length > 0) setPlanSlug(plans[0].slug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planSlug, plans]);

  // Preferences that actually have a customer-published week for this plan.
  // The plan's configured preference options can still reference a retired
  // preference code, so the published weeks are an additional source of truth.
  const publishedPrefsQ = useQuery({
    enabled: Boolean(planSlug),
    queryKey: ["public-weekly-week-prefs", planSlug],
    queryFn: () => listWeekPrefsFn({ data: { plan_slug: planSlug! } }),
    staleTime: 60_000,
  });
  const publishedPrefCodes = useMemo(
    () => new Set((publishedPrefsQ.data ?? []).map((p) => p.code)),
    [publishedPrefsQ.data],
  );

  const planPrefs = useMemo(() => {
    if (!catalog || !planSlug) return [] as { code: string; label: string }[];
    const plan = catalog.plans.find((p) => p.slug === planSlug);
    const allowed = plan?.allowed_preference_codes ?? [];
    return catalog.food_preferences
      .filter(
        (p) =>
          p.is_active !== false &&
          (allowed.length === 0 ||
            allowed.includes(p.code) ||
            publishedPrefCodes.has(p.code)),
      )
      .map((p) => ({ code: p.code, label: p.label }));
  }, [catalog, planSlug, publishedPrefCodes]);


  useEffect(() => {
    if (planPrefs.length === 0) return;
    if (!preference || !planPrefs.find((p) => p.code === preference)) {
      setPreference(planPrefs[0].code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planPrefs, preference]);

  const weeksQ = useQuery({
    enabled: Boolean(planSlug && preference),
    queryKey: ["public-weekly-weeks", planSlug, preference],
    queryFn: () =>
      listWeeksFn({ data: { plan_slug: planSlug!, preference: preference! } }),
  });
  const weeks = (weeksQ.data ?? []) as PublishedWeek[];

  useEffect(() => {
    if (weeks.length === 0) return;
    if (!weekStart || !weeks.find((w) => w.week_start_date === weekStart)) {
      setWeekStart(weeks[0].week_start_date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeks, weekStart]);

  const menuQ = useQuery({
    enabled: Boolean(planSlug && preference && weekStart),
    queryKey: ["public-weekly-menu", planSlug, preference, weekStart],
    queryFn: () =>
      getMenuFn({
        data: {
          plan_slug: planSlug!,
          preference: preference!,
          week_start: weekStart!,
        },
      }),
  });
  const menu = (menuQ.data ?? null) as WeeklyMenuJson | null;
  const grid = useMemo(() => normaliseGrid(menu), [menu]);
  const nutritionFn = useServerFn(getPublicDishNutrition);
  const dishIds = useMemo(() => {
    const ids = new Set<string>();
    for (const day of Object.values(grid)) {
      for (const c of Object.values(day)) {
        if (!c) continue;
        for (const d of c.dishes && c.dishes.length ? c.dishes : [c]) {
          if (d.dish_id) ids.add(d.dish_id);
        }
      }
    }
    return [...ids].sort();
  }, [grid]);
  const nutritionQ = useQuery({
    queryKey: ["public-dish-nutrition", dishIds],
    queryFn: () => nutritionFn({ data: { dish_ids: dishIds } }),
    enabled: dishIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const nutrition = nutritionQ.data ?? {};

  const invalidSelection =
    !catalogLoading && plans.length > 0 && planSlug && planPrefs.length === 0;

  return (
    <NutritionCtx.Provider value={nutrition}>
    <SiteLayout>
      <PageHero
        eyebrow="Meal Plans 2.0"
        title="Weekly Menu"
        intro="Monday to Sunday — see exactly what's on the plate this week and next."
      />
      <Section>
        <Container>
          <div className="mb-6 grid gap-3 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Plan
              </label>
              <Select
                value={planSlug ?? ""}
                onValueChange={(v) => setPlanSlug(v || null)}
                disabled={catalogLoading || plans.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a plan" />
                </SelectTrigger>
                <SelectContent>
                  {plans.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {p.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Food preference
              </label>
              <Select
                value={preference ?? ""}
                onValueChange={(v) => setPreference(v || null)}
                disabled={planPrefs.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a preference" />
                </SelectTrigger>
                <SelectContent>
                  {planPrefs.map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Week
              </label>
              <div className="flex flex-wrap gap-2">
                {weeksQ.isLoading ? (
                  <span className="text-sm text-muted-foreground">Loading…</span>
                ) : weeksQ.isError ? (
                  <Button size="sm" variant="outline" onClick={() => weeksQ.refetch()}>
                    Retry
                  </Button>
                ) : weeks.length === 0 ? (
                  <span className="text-sm text-muted-foreground">
                    No published weeks yet.
                  </span>
                ) : (
                  weeks.slice(0, 4).map((w) => (
                    <Button
                      key={w.cycle_id}
                      size="sm"
                      variant={w.week_start_date === weekStart ? "default" : "outline"}
                      onClick={() => setWeekStart(w.week_start_date)}
                    >
                      {w.week_start_date}
                      <Badge variant="secondary" className="ml-2">
                        {w.month_label}
                      </Badge>
                    </Button>
                  ))
                )}
              </div>
            </div>
          </div>

          <WeekView
            state={
              catalogLoading || (!planSlug && plans.length === 0 && !catalog)
                ? "loading"
                : invalidSelection
                ? "invalid"
                : !planSlug || !preference
                ? "invalid"
                : weeksQ.isError
                ? "weeks-error"
                : weeksQ.isLoading
                ? "loading"
                : weeks.length === 0
                ? "no-weeks"
                : menuQ.isError
                ? "menu-error"
                : menuQ.isLoading
                ? "loading"
                : !menu
                ? "no-menu"
                : "ready"
            }
            grid={grid}
            onRetry={() => {
              if (weeksQ.isError) weeksQ.refetch();
              if (menuQ.isError) menuQ.refetch();
            }}
          />
        </Container>
      </Section>
    </SiteLayout>
    </NutritionCtx.Provider>
  );
}

type ViewState =
  | "loading"
  | "invalid"
  | "weeks-error"
  | "no-weeks"
  | "menu-error"
  | "no-menu"
  | "ready";

function WeekView({
  state,
  grid,
  onRetry,
}: {
  state: ViewState;
  grid: Record<number, Record<Slot, Cell>>;
  onRetry: () => void;
}) {
  if (state === "loading") {
    return (
      <div className="space-y-4">
        {DAYS_FULL.map((d) => (
          <div
            key={d}
            className="h-40 animate-pulse rounded-2xl border border-border bg-card/60"
          />
        ))}
      </div>
    );
  }
  if (state === "invalid") {
    return (
      <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Pick a plan and food preference to see the published menu.
      </p>
    );
  }
  if (state === "weeks-error" || state === "menu-error") {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-foreground">
          We couldn't load the menu. Please try again.
        </p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }
  if (state === "no-weeks") {
    return (
      <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        No published weeks yet for this plan and preference. Please check back soon.
      </p>
    );
  }
  if (state === "no-menu") {
    return (
      <p className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        No menu is published for the selected filters yet.
      </p>
    );
  }
  return (
    <div className="space-y-6">
      {DAYS_FULL.map((label, idx) => {
        const slots = grid[idx];
        const hasScheduledDish = SLOTS.some((slot) => Boolean(slots[slot]?.dish_name));
        return hasScheduledDish ? <DayCard key={label} label={label} slots={slots} /> : null;
      })}
    </div>
  );
}

function DayCard({
  label,
  slots,
}: {
  label: string;
  slots: Record<Slot, Cell>;
}) {
  return (
    <article className="rounded-2xl border border-border bg-card p-4 shadow-sm md:p-6">
      <header className="mb-4 flex items-baseline justify-between">
        <h2 className="font-display text-xl font-semibold text-foreground md:text-2xl">
          {label}
        </h2>
      </header>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {SLOTS.map((slot) => (
          <SlotSection key={slot} slot={slot} cell={slots[slot]} />
        ))}
      </div>
    </article>
  );
}

function SlotSection({ slot, cell }: { slot: Slot; cell: Cell }) {
  const slotLabel = slot.charAt(0).toUpperCase() + slot.slice(1);
  const dishes: DishView[] = cell
    ? cell.dishes && cell.dishes.length > 0
      ? cell.dishes
      : cell.dish_name
        ? [cell]
        : []
    : [];
  return (
    <section className="min-w-0">
      <h3 className="mb-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
        {slotLabel}
      </h3>
      {dishes.length === 0 ? (
        <div className="rounded-xl border border-border bg-background p-3">
          <p className="text-xs text-muted-foreground">No meal scheduled</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {dishes.map((d, i) => (
            <DishCard key={`${slot}-${i}`} slotLabel={slotLabel} dish={d} />
          ))}
        </div>
      )}
    </section>
  );
}

function DishCard({ slotLabel, dish }: { slotLabel: string; dish: DishView }) {
  const imgSrc = dish.image_url || mealPlaceholder;
  const alt = dish.alt_text?.trim() || dish.dish_name || `${slotLabel} placeholder`;
  const nutritionMap = useContext(NutritionCtx);
  const n = dish.dish_id ? nutritionMap[dish.dish_id] : undefined;
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-border bg-background">
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <img
          src={imgSrc}
          alt={alt}
          loading="lazy"
          onError={(e) => {
            const el = e.currentTarget;
            if (el.src !== mealPlaceholder) el.src = mealPlaceholder;
          }}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <h4 className="break-words text-sm font-medium leading-snug text-foreground">
          {dish.dish_name}
        </h4>
        {dish.description ? (
          <p className="text-xs leading-relaxed text-muted-foreground">{dish.description}</p>
        ) : null}
        <DishNutrition
          calories={n?.calories_kcal}
          protein={n?.protein_g}
          className="mt-1"
        />
      </div>
    </div>
  );
}